<template>
  <nav :class="{ scrolled: isScrolled }">
    <div class="nav-container">
      <!-- Logo -->
      <router-link to="/" class="logo" aria-label="Fulfilldrop Logo">
        <img src="@/assets/logo-icon.png" alt="" class="logo-icon" aria-hidden="true" />
        <span class="logo-text">Fulfilldrop</span>
      </router-link>

      <!-- Desktop Nav -->
      <ul class="nav-links" v-if="!isMobile">
        <!-- Solutions Mega Menu -->
        <li class="nav-item has-megamenu"
            @mouseenter="openMega('solutions')"
            @mouseleave="closeMegaDelay">
          <a href="#" class="nav-link" :class="{ active: activeMega === 'solutions' }">
            {{ t.nav.solutions }}
            <svg class="chevron" viewBox="0 0 20 20" fill="currentColor"><path fill-rule="evenodd" d="M5.23 7.21a.75.75 0 011.06.02L10 11.168l3.71-3.938a.75.75 0 111.08 1.04l-4.25 4.5a.75.75 0 01-1.08 0l-4.25-4.5a.75.75 0 01.02-1.06z"/></svg>
          </a>
          <div class="megamenu" :class="{ show: activeMega === 'solutions' }"
               @mouseenter="openMega('solutions')"
               @mouseleave="closeMegaDelay">
            <div class="megamenu-inner">
              <div class="megamenu-col">
                <h4 class="megamenu-col-title">{{ t.megamenu.solByType }}</h4>
                <router-link to="/solutions" class="megamenu-item">
                  <div class="megamenu-item-icon" style="background:#e6f7f9;"><i class="fa-solid fa-paper-plane"></i></div>
                  <div><div class="megamenu-item-label">Dropshipping</div><div class="megamenu-item-desc">End-to-end China sourcing &amp; delivery.</div></div>
                </router-link>
                <router-link to="/solutions" class="megamenu-item">
                  <div class="megamenu-item-icon" style="background:#f0fdf4;"><i class="fa-solid fa-warehouse"></i></div>
                  <div><div class="megamenu-item-label">Warehousing</div><div class="megamenu-item-desc">Global storage &amp; fulfillment.</div></div>
                </router-link>
                <router-link to="/solutions" class="megamenu-item">
                  <div class="megamenu-item-icon" style="background:#fef3c7;"><i class="fa-solid fa-shipping-fast"></i></div>
                  <div><div class="megamenu-item-label">Global Logistics</div><div class="megamenu-item-desc">International shipping &amp; tracking.</div></div>
                </router-link>
                <router-link to="/solutions" class="megamenu-item">
                  <div class="megamenu-item-icon" style="background:#fce7f3;"><i class="fa-solid fa-magnifying-glass"></i></div>
                  <div><div class="megamenu-item-label">{{ t.megamenu.platQC }}</div><div class="megamenu-item-desc">Multi-stage inspection.</div></div>
                </router-link>
              </div>
              <div class="megamenu-col">
                <h4 class="megamenu-col-title">By Business Model</h4>
                <router-link to="/solutions" class="megamenu-item">
                  <div class="megamenu-item-icon" style="background:#e6f7f9;"><i class="fa-brands fa-amazon"></i></div>
                  <div><div class="megamenu-item-label">Amazon Sellers</div><div class="megamenu-item-desc">FBA prep &amp; fulfillment.</div></div>
                </router-link>
                <router-link to="/solutions" class="megamenu-item">
                  <div class="megamenu-item-icon" style="background:#f0fdf4;"><i class="fa-brands fa-shopify"></i></div>
                  <div><div class="megamenu-item-label">{{ t.megamenu.platShopify }} Brands</div><div class="megamenu-item-desc">{{ t.megamenu.platShopifyDesc }}</div></div>
                </router-link>
                <router-link to="/solutions" class="megamenu-item">
                  <div class="megamenu-item-icon" style="background:#fef3c7;"><i class="fa-brands fa-tiktok"></i></div>
                  <div><div class="megamenu-item-label">TikTok / IG Shops</div><div class="megamenu-item-desc">{{ t.megamenu.platTikTokDesc }}</div></div>
                </router-link>
                <router-link to="/solutions" class="megamenu-item">
                  <div class="megamenu-item-icon" style="background:#dbeafe;"><i class="fa-solid fa-rocket"></i></div>
                  <div><div class="megamenu-item-label">Crowdfunding</div><div class="megamenu-item-desc">Kickstarter backer fulfillment.</div></div>
                </router-link>
              </div>
              <div class="megamenu-col megamenu-col--featured">
                <h4 class="megamenu-col-title">{{ t.megamenu.solPopular }}</h4>
                <div class="megamenu-featured-card">
                  <span class="megamenu-featured-badge">{{ t.megamenu.solMostPopular }}</span>
                  <h5>{{ t.megamenu.solBrandedPackaging }}</h5>
                  <p>{{ t.megamenu.solBrandedPackagingDesc }}</p>
                  <router-link to="/solutions" class="megamenu-featured-link">{{ t.megamenu.solExplore }}</router-link>
                </div>
                <div class="megamenu-featured-card">
                  <span class="megamenu-featured-badge megamenu-featured-badge--new">{{ t.megamenu.solNew }}</span>
                  <h5>{{ t.megamenu.solAPI }}</h5>
                  <p>{{ t.megamenu.solAPIDesc }}</p>
                  <router-link to="/products" class="megamenu-featured-link">{{ t.megamenu.solLearnMore }}</router-link>
                </div>
              </div>
            </div>
          </div>
        </li>

        <!-- Platform -->
        <li class="nav-item has-megamenu"
            @mouseenter="openMega('platform')"
            @mouseleave="closeMegaDelay">
          <a href="#" class="nav-link" :class="{ active: activeMega === 'platform' }">
            {{ t.nav.platform || 'Platform' }}
            <svg class="chevron" viewBox="0 0 20 20" fill="currentColor"><path fill-rule="evenodd" d="M5.23 7.21a.75.75 0 011.06.02L10 11.168l3.71-3.938a.75.75 0 111.08 1.04l-4.25 4.5a.75.75 0 01-1.08 0l-4.25-4.5a.75.75 0 01.02-1.06z"/></svg>
          </a>
          <div class="megamenu" :class="{ show: activeMega === 'platform' }"
               @mouseenter="openMega('platform')"
               @mouseleave="closeMegaDelay">
            <div class="megamenu-inner">
              <div class="megamenu-col">
                <h4 class="megamenu-col-title">{{ t.megamenu.platCoreFeatures }}</h4>
                <router-link to="/products" class="megamenu-item">
                  <div class="megamenu-item-icon" style="background:#e6f7f9;"><i class="fa-solid fa-cart-shopping"></i></div>
                  <div><div class="megamenu-item-label">{{ t.megamenu.platOrderMgmt }}</div><div class="megamenu-item-desc">Sync &amp; manage orders across all channels.</div></div>
                </router-link>
                <router-link to="/products" class="megamenu-item">
                  <div class="megamenu-item-icon" style="background:#f0fdf4;"><i class="fa-solid fa-boxes-stacked"></i></div>
                  <div><div class="megamenu-item-label">{{ t.megamenu.platInventory }}</div><div class="megamenu-item-desc">{{ t.megamenu.platInventoryDesc }}</div></div>
                </router-link>
                <router-link to="/products" class="megamenu-item">
                  <div class="megamenu-item-icon" style="background:#fef3c7;"><i class="fa-solid fa-magnifying-glass"></i></div>
                  <div><div class="megamenu-item-label">Quality Control</div><div class="megamenu-item-desc">{{ t.megamenu.platQCDesc }}</div></div>
                </router-link>
                <router-link to="/products" class="megamenu-item">
                  <div class="megamenu-item-icon" style="background:#fce7f3;"><i class="fa-solid fa-chart-line"></i></div>
                  <div><div class="megamenu-item-label">{{ t.megamenu.platAnalytics }}</div><div class="megamenu-item-desc">{{ t.megamenu.platAnalyticsDesc }}</div></div>
                </router-link>
              </div>
              <div class="megamenu-col">
                <h4 class="megamenu-col-title">{{ t.megamenu.platIntegrations }}</h4>
                <router-link to="/products" class="megamenu-item">
                  <div class="megamenu-item-icon" style="background:#e6f7f9;"><i class="fa-brands fa-shopify"></i></div>
                  <div><div class="megamenu-item-label">Shopify</div><div class="megamenu-item-desc">Seamless store integration.</div></div>
                </router-link>
                <router-link to="/products" class="megamenu-item">
                  <div class="megamenu-item-icon" style="background:#f0fdf4;"><i class="fa-brands fa-wordpress"></i></div>
                  <div><div class="megamenu-item-label">{{ t.megamenu.platWoo }}</div><div class="megamenu-item-desc">{{ t.megamenu.platWooDesc }}</div></div>
                </router-link>
                <router-link to="/products" class="megamenu-item">
                  <div class="megamenu-item-icon" style="background:#fef3c7;"><i class="fa-brands fa-amazon"></i></div>
                  <div><div class="megamenu-item-label">{{ t.megamenu.platAmazon }}</div><div class="megamenu-item-desc">FBA prep &amp; direct ship.</div></div>
                </router-link>
                <router-link to="/products" class="megamenu-item">
                  <div class="megamenu-item-icon" style="background:#dbeafe;"><i class="fa-brands fa-tiktok"></i></div>
                  <div><div class="megamenu-item-label">{{ t.megamenu.platTikTok }}</div><div class="megamenu-item-desc">Social commerce fulfillment.</div></div>
                </router-link>
              </div>
              <div class="megamenu-col">
                <h4 class="megamenu-col-title">{{ t.megamenu.platTechnology }}</h4>
                <router-link to="/products" class="megamenu-item">
                  <div class="megamenu-item-icon" style="background:#e6f7f9;"><i class="fa-solid fa-code"></i></div>
                  <div><div class="megamenu-item-label">{{ t.megamenu.platREST }}</div><div class="megamenu-item-desc">{{ t.megamenu.platRESTDesc }}</div></div>
                </router-link>
                <router-link to="/products" class="megamenu-item">
                  <div class="megamenu-item-icon" style="background:#f0fdf4;"><i class="fa-solid fa-bolt"></i></div>
                  <div><div class="megamenu-item-label">Webhooks</div><div class="megamenu-item-desc">{{ t.megamenu.platWebhooksDesc }}</div></div>
                </router-link>
                <router-link to="/products" class="megamenu-item">
                  <div class="megamenu-item-icon" style="background:#fef3c7;"><i class="fa-solid fa-shield-halved"></i></div>
                  <div><div class="megamenu-item-label">{{ t.megamenu.platSecurity }}</div><div class="megamenu-item-desc">{{ t.megamenu.platSecurityDesc }}</div></div>
                </router-link>
                <router-link to="/products" class="megamenu-item">
                  <div class="megamenu-item-icon" style="background:#fce7f3;"><i class="fa-solid fa-satellite-dish"></i></div>
                  <div><div class="megamenu-item-label">{{ t.megamenu.platTracking }}</div><div class="megamenu-item-desc">{{ t.megamenu.platTrackingDesc }}</div></div>
                </router-link>
              </div>
            </div>
          </div>
        </li>

        <!-- Resources -->
        <li class="nav-item has-megamenu"
            @mouseenter="openMega('resources')"
            @mouseleave="closeMegaDelay">
          <router-link to="/resources" class="nav-link" :class="{ active: activeMega === 'resources' }">
            {{ t.nav.resources || 'Resources' }}
            <svg class="chevron" viewBox="0 0 20 20" fill="currentColor"><path fill-rule="evenodd" d="M5.23 7.21a.75.75 0 011.06.02L10 11.168l3.71-3.938a.75.75 0 111.08 1.04l-4.25 4.5a.75.75 0 01-1.08 0l-4.25-4.5a.75.75 0 01.02-1.06z"/></svg>
          </router-link>
          <div class="megamenu megamenu--sm" :class="{ show: activeMega === 'resources' }"
               @mouseenter="openMega('resources')"
               @mouseleave="closeMegaDelay">
            <div class="megamenu-inner">
              <div class="megamenu-col">
                <h4 class="megamenu-col-title">Learn</h4>
                <router-link to="/faq" class="megamenu-item">
                  <div class="megamenu-item-icon" style="background:#e6f7f9;"><i class="fa-solid fa-circle-question"></i></div>
                  <div><div class="megamenu-item-label">{{ t.megamenu.resHelpCenter }}</div><div class="megamenu-item-desc">FAQs, guides &amp; tutorials.</div></div>
                </router-link>
                <a href="#" class="megamenu-item" @click.prevent>
                  <div class="megamenu-item-icon" style="background:#f0fdf4;"><i class="fa-solid fa-book-open"></i></div>
                  <div><div class="megamenu-item-label">{{ t.megamenu.resGuides }}</div><div class="megamenu-item-desc">{{ t.megamenu.resGuidesDesc }}</div></div>
                </a>
                <a href="#" class="megamenu-item" @click.prevent>
                  <div class="megamenu-item-icon" style="background:#fef3c7;"><i class="fa-solid fa-video"></i></div>
                  <div><div class="megamenu-item-label">{{ t.megamenu.resVideoDemos }}</div><div class="megamenu-item-desc">{{ t.megamenu.resVideoDemosDesc }}</div></div>
                </a>
              </div>
              <div class="megamenu-col">
                <h4 class="megamenu-col-title">{{ t.megamenu.resInsights }}</h4>
                <a href="#" class="megamenu-item" @click.prevent>
                  <div class="megamenu-item-icon" style="background:#e6f7f9;"><i class="fa-solid fa-pen-nib"></i></div>
                  <div><div class="megamenu-item-label">{{ t.megamenu.resBlog }}</div><div class="megamenu-item-desc">Industry trends &amp; tips.</div></div>
                </a>
                <a href="#" class="megamenu-item" @click.prevent>
                  <div class="megamenu-item-icon" style="background:#f0fdf4;"><i class="fa-solid fa-building"></i></div>
                  <div><div class="megamenu-item-label">{{ t.megamenu.resCaseStudies }}</div><div class="megamenu-item-desc">{{ t.megamenu.resCaseStudiesDesc }}</div></div>
                </a>
                <a href="#" class="megamenu-item" @click.prevent>
                  <div class="megamenu-item-icon" style="background:#fef3c7;"><i class="fa-solid fa-newspaper"></i></div>
                  <div><div class="megamenu-item-label">{{ t.megamenu.resEbooks }}</div><div class="megamenu-item-desc">{{ t.megamenu.resEbooksDesc }}</div></div>
                </a>
              </div>
              <div class="megamenu-col">
                <h4 class="megamenu-col-title">{{ t.megamenu.resDevelopers }}</h4>
                <a href="#" class="megamenu-item" @click.prevent>
                  <div class="megamenu-item-icon" style="background:#e6f7f9;"><i class="fa-solid fa-laptop-code"></i></div>
                  <div><div class="megamenu-item-label">{{ t.megamenu.resAPIDocs }}</div><div class="megamenu-item-desc">{{ t.megamenu.resAPIDocsDesc }}</div></div>
                </a>
                <a href="#" class="megamenu-item" @click.prevent>
                  <div class="megamenu-item-icon" style="background:#f0fdf4;"><i class="fa-solid fa-plug"></i></div>
                  <div><div class="megamenu-item-label">{{ t.megamenu.resIntegration }}</div><div class="megamenu-item-desc">{{ t.megamenu.resIntegrationDesc }}</div></div>
                </a>
                <a href="#" class="megamenu-item" @click.prevent>
                  <div class="megamenu-item-icon" style="background:#fef3c7;"><i class="fa-solid fa-signal"></i></div>
                  <div><div class="megamenu-item-label">{{ t.megamenu.resStatus }}</div><div class="megamenu-item-desc">System health &amp; uptime.</div></div>
                </a>
              </div>
            </div>
          </div>
        </li>

        <!-- {{ t.megamenu.compCompany }} -->
        <li class="nav-item has-megamenu"
            @mouseenter="openMega('company')"
            @mouseleave="closeMegaDelay">
          <a href="#" class="nav-link" :class="{ active: activeMega === 'company' }">
            {{ t.nav.about }}
            <svg class="chevron" viewBox="0 0 20 20" fill="currentColor"><path fill-rule="evenodd" d="M5.23 7.21a.75.75 0 011.06.02L10 11.168l3.71-3.938a.75.75 0 111.08 1.04l-4.25 4.5a.75.75 0 01-1.08 0l-4.25-4.5a.75.75 0 01.02-1.06z"/></svg>
          </a>
          <div class="megamenu megamenu--sm" :class="{ show: activeMega === 'company' }"
               @mouseenter="openMega('company')"
               @mouseleave="closeMegaDelay">
            <div class="megamenu-inner">
              <div class="megamenu-col">
                <h4 class="megamenu-col-title">Company</h4>
                <router-link to="/about" class="megamenu-item">
                  <div class="megamenu-item-icon" style="background:#e6f7f9;"><i class="fa-solid fa-star"></i></div>
                  <div><div class="megamenu-item-label">{{ t.megamenu.compAbout }}</div><div class="megamenu-item-desc">Our mission &amp; story.</div></div>
                </router-link>
                <a href="#" class="megamenu-item" @click.prevent>
                  <div class="megamenu-item-icon" style="background:#f0fdf4;"><i class="fa-solid fa-people-group"></i></div>
                  <div><div class="megamenu-item-label">{{ t.megamenu.compTeam }}</div><div class="megamenu-item-desc">{{ t.megamenu.compTeamDesc }}</div></div>
                </a>
                <a href="#" class="megamenu-item" @click.prevent>
                  <div class="megamenu-item-icon" style="background:#fef3c7;"><i class="fa-solid fa-briefcase"></i></div>
                  <div><div class="megamenu-item-label">{{ t.megamenu.compCareers }}</div><div class="megamenu-item-desc">{{ t.megamenu.compCareersDesc }}</div></div>
                </a>
              </div>
              <div class="megamenu-col">
                <h4 class="megamenu-col-title">Trust</h4>
                <a href="#" class="megamenu-item" @click.prevent>
                  <div class="megamenu-item-icon" style="background:#e6f7f9;"><i class="fa-solid fa-shield-halved"></i></div>
                  <div><div class="megamenu-item-label">Security</div><div class="megamenu-item-desc">How we protect your data.</div></div>
                </a>
                <a href="#" class="megamenu-item" @click.prevent>
                  <div class="megamenu-item-icon" style="background:#f0fdf4;"><i class="fa-solid fa-file-contract"></i></div>
                  <div><div class="megamenu-item-label">Terms of Service</div><div class="megamenu-item-desc">Legal terms &amp; conditions.</div></div>
                </a>
                <a href="#" class="megamenu-item" @click.prevent>
                  <div class="megamenu-item-icon" style="background:#fef3c7;"><i class="fa-solid fa-lock"></i></div>
                  <div><div class="megamenu-item-label">Privacy Policy</div><div class="megamenu-item-desc">Data handling practices.</div></div>
                </a>
              </div>
              <div class="megamenu-col">
                <h4 class="megamenu-col-title">Contact</h4>
                <router-link to="/contact" class="megamenu-item">
                  <div class="megamenu-item-icon" style="background:#e6f7f9;"><i class="fa-solid fa-envelope"></i></div>
                  <div><div class="megamenu-item-label">Contact Us</div><div class="megamenu-item-desc">{{ t.megamenu.compContactDesc }}</div></div>
                </router-link>
                <a href="#" class="megamenu-item" @click.prevent>
                  <div class="megamenu-item-icon" style="background:#f0fdf4;"><i class="fa-solid fa-headset"></i></div>
                  <div><div class="megamenu-item-label">24/7 Support</div><div class="megamenu-item-desc">Always here to help.</div></div>
                </a>
              </div>
            </div>
          </div>
        </li>

        <!-- Pricing -->
        <li class="nav-item">
          <router-link to="/pricing" class="nav-link">{{ t.nav.pricing || 'Pricing' }}</router-link>
        </li>

        <!-- FAQ -->
        <li class="nav-item">
          <router-link to="/faq" class="nav-link">{{ t.nav.faq }}</router-link>
        </li>
      </ul>

      <!-- Right Actions -->
      <div class="nav-actions">
        <div class="language-dropdown" :class="{ show: showLangDropdown }">
          <button class="dropdown-toggle" @click="toggleLangDropdown">
            <span>{{ getCurrentLangLabel() }}</span>
            <svg class="dropdown-arrow" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M6 9l6 6 6-6"/></svg>
          </button>
          <div class="dropdown-content" v-show="showLangDropdown">
            <button @click="selectLang('en')" class="dropdown-item">EN</button>
            <button @click="selectLang('es')" class="dropdown-item">ES</button>
            <button @click="selectLang('fr')" class="dropdown-item">FR</button>
            <!-- <button @click="selectLang('zh')" class="dropdown-item">ZH</button> -->
            <button @click="selectLang('ja')" class="dropdown-item">JA</button>
            <button @click="selectLang('de')" class="dropdown-item">DE</button>
            <button @click="selectLang('pt')" class="dropdown-item">PT</button>
            <button @click="selectLang('it')" class="dropdown-item">IT</button>
            <button @click="selectLang('ar')" class="dropdown-item">AR</button>
          </div>
        </div>
        <button class="cta-btn" @click="scrollToContact">{{ t.nav.getStarted }}</button>
      </div>

      <!-- Mobile Hamburger -->
      <button class="hamburger" :class="{ active: isMobileMenuOpen }" @click="toggleMobileMenu" v-if="isMobile">
        <span></span><span></span><span></span>
      </button>
    </div>

    <!-- Mobile Menu -->
    <transition name="mobile-menu">
      <div class="mobile-menu" v-if="isMobileMenuOpen">
        <div class="mobile-menu-inner">
          <div class="mobile-menu-section">
            <div class="mobile-menu-heading">Solutions</div>
            <router-link to="/solutions" class="mobile-menu-item" @click="closeMobileMenu">Dropshipping</router-link>
            <router-link to="/solutions" class="mobile-menu-item" @click="closeMobileMenu">Warehousing</router-link>
            <router-link to="/solutions" class="mobile-menu-item" @click="closeMobileMenu">Logistics</router-link>
            <router-link to="/solutions" class="mobile-menu-item" @click="closeMobileMenu">Quality Control</router-link>
          </div>
          <div class="mobile-menu-section">
            <div class="mobile-menu-heading">Platform</div>
            <router-link to="/products" class="mobile-menu-item" @click="closeMobileMenu">Features</router-link>
            <router-link to="/products" class="mobile-menu-item" @click="closeMobileMenu">Integrations</router-link>
            <router-link to="/products" class="mobile-menu-item" @click="closeMobileMenu">API</router-link>
          </div>
          <div class="mobile-menu-section">
            <div class="mobile-menu-heading">Company</div>
            <router-link to="/about" class="mobile-menu-item" @click="closeMobileMenu">About Us</router-link>
            <router-link to="/contact" class="mobile-menu-item" @click="closeMobileMenu">Contact</router-link>
          </div>
          <div class="mobile-menu-section">
            <router-link to="/pricing" class="mobile-menu-item" @click="closeMobileMenu">Pricing</router-link>
            <router-link to="/faq" class="mobile-menu-item" @click="closeMobileMenu">FAQ</router-link>
          </div>
          <button class="cta-btn mobile-cta" @click="closeMobileMenu">{{ t.nav.getStarted }}</button>
        </div>
      </div>
    </transition>
  </nav>
</template>

<script>
import { getSafeTranslator } from '../assets/languages.js'

export default {
  name: 'Navbar',
  props: {
    currentLang: { type: String, default: 'en' }
  },
  data() {
    return {
      showLangDropdown: false,
      isScrolled: false,
      isMobile: false,
      isMobileMenuOpen: false,
      activeMega: null,
      megaTimer: null
    }
  },
  computed: {
    t() {
      return getSafeTranslator(this.currentLang)
    }
  },
  methods: {
    changeLang(lang) {
      this.$emit('change-lang', lang)
    },
    toggleLangDropdown() {
      this.showLangDropdown = !this.showLangDropdown
    },
    selectLang(lang) {
      this.changeLang(lang)
      this.showLangDropdown = false
    },
    getCurrentLangLabel() {
      const labels = { en:'EN', es:'ES', fr:'FR', zh:'ZH', ja:'JA', de:'DE', pt:'PT', it:'IT', ar:'AR' }
      return labels[this.currentLang] || 'EN'
    },
    scrollToContact() {
      const el = document.getElementById('contact')
      if (el) el.scrollIntoView({ behavior: 'smooth' })
    },
    openMega(name) {
      clearTimeout(this.megaTimer)
      this.activeMega = name
    },
    closeMegaDelay() {
      this.megaTimer = setTimeout(() => { this.activeMega = null }, 200)
    },
    handleScroll() {
      this.isScrolled = window.scrollY > 20
    },
    handleResize() {
      this.isMobile = window.innerWidth < 1024
      if (!this.isMobile) this.isMobileMenuOpen = false
    },
    toggleMobileMenu() {
      this.isMobileMenuOpen = !this.isMobileMenuOpen
    },
    closeMobileMenu() {
      this.isMobileMenuOpen = false
    },
    handleClickOutside(e) {
      const dd = this.$el.querySelector('.language-dropdown')
      if (dd && !dd.contains(e.target)) this.showLangDropdown = false
    }
  },
  mounted() {
    this.handleResize()
    window.addEventListener('scroll', this.handleScroll)
    window.addEventListener('resize', this.handleResize)
    document.addEventListener('click', this.handleClickOutside)
  },
  beforeUnmount() {
    window.removeEventListener('scroll', this.handleScroll)
    window.removeEventListener('resize', this.handleResize)
    document.removeEventListener('click', this.handleClickOutside)
    clearTimeout(this.megaTimer)
  }
}
</script>

<style scoped>
nav {
  background: rgba(255,255,255,0.95);
  backdrop-filter: blur(12px);
  -webkit-backdrop-filter: blur(12px);
  box-shadow: 0 1px 3px rgba(0,0,0,0.06);
  position: sticky;
  top: 0;
  z-index: 1000;
  transition: box-shadow 0.3s, background 0.3s;
}
nav.scrolled {
  background: rgba(255,255,255,0.99);
  box-shadow: 0 2px 16px rgba(0,0,0,0.08);
}
.nav-container {
  max-width: 1280px;
  margin: 0 auto;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0 2rem;
  height: 68px;
}
.logo {
  flex-shrink: 0;
  display: flex;
  align-items: center;
  gap: 10px;
  text-decoration: none;
}
.logo-icon {
  height: 36px;
  width: auto;
  display: block;
}
.logo-text {
  font-family: 'Inter', -apple-system, BlinkMacSystemFont, sans-serif;
  font-size: 1.25rem;
  font-weight: 700;
  color: #1e293b;
  letter-spacing: -0.02em;
}

/* ---- Nav Links ---- */
.nav-links {
  display: flex;
  list-style: none;
  align-items: center;
  gap: 0;
  margin: 0;
  padding: 0;
}
.nav-item {
  position: relative;
}
.nav-link {
  display: flex;
  align-items: center;
  gap: 4px;
  padding: 8px 16px;
  color: #334155;
  text-decoration: none;
  font-weight: 500;
  font-size: 0.92rem;
  border-radius: 8px;
  transition: color 0.2s, background 0.2s;
  white-space: nowrap;
}
.nav-link:hover,
.nav-link.active {
  color: #28b5c1;
  background: #f1f0ff;
}
.chevron {
  width: 14px;
  height: 14px;
  transition: transform 0.2s;
}
.nav-link.active .chevron {
  transform: rotate(180deg);
}

/* ---- Mega Menu ---- */
.megamenu {
  display: block;
  position: absolute;
  top: 100%;
  left: 50%;
  transform: translateX(-50%) translateY(8px);
  background: #fff;
  border-radius: 16px;
  box-shadow: 0 20px 60px rgba(0,0,0,0.12), 0 2px 8px rgba(0,0,0,0.06);
  padding: 28px 32px;
  min-width: 900px;
  opacity: 0;
  visibility: hidden;
  transition: opacity 0.22s, transform 0.22s, visibility 0.22s;
  z-index: 1001;
  pointer-events: none;
  border: 1px solid #f0f0f5;
}
.megamenu.show {
  opacity: 1;
  visibility: visible;
  transform: translateX(-50%) translateY(4px);
  pointer-events: auto;
}
.megamenu--sm {
  min-width: 720px;
}
.megamenu-inner {
  display: flex;
  gap: 32px;
}
.megamenu-col {
  flex: 1;
  min-width: 0;
}
.megamenu-col-title {
  font-size: 0.72rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  color: #94a3b8;
  margin-bottom: 14px;
  padding-bottom: 8px;
  border-bottom: 1px solid #f1f5f9;
}
.megamenu-item {
  display: flex;
  align-items: flex-start;
  gap: 12px;
  padding: 10px 8px;
  border-radius: 10px;
  text-decoration: none;
  transition: background 0.15s;
  cursor: pointer;
}
.megamenu-item:hover {
  background: #f8f7ff;
}
.megamenu-item-icon {
  width: 36px;
  height: 36px;
  border-radius: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  font-size: 0.85rem;
  color: #28b5c1;
}
.megamenu-item-label {
  font-size: 0.88rem;
  font-weight: 600;
  color: #1e293b;
  line-height: 1.3;
}
.megamenu-item-desc {
  font-size: 0.78rem;
  color: #94a3b8;
  margin-top: 2px;
  line-height: 1.4;
}

/* Featured Col */
.megamenu-col--featured {
  background: linear-gradient(135deg, #f8f7ff 0%, #f0fdf4 100%);
  border-radius: 14px;
  padding: 20px;
  min-width: 240px;
}
.megamenu-col--featured .megamenu-col-title {
  border-bottom-color: rgba(40,181,193,0.1);
}
.megamenu-featured-card {
  background: #fff;
  border-radius: 12px;
  padding: 16px;
  margin-bottom: 12px;
  box-shadow: 0 2px 8px rgba(0,0,0,0.04);
  transition: box-shadow 0.2s;
}
.megamenu-featured-card:hover {
  box-shadow: 0 4px 16px rgba(40,181,193,0.1);
}
.megamenu-featured-badge {
  display: inline-block;
  background: #28b5c1;
  color: #fff;
  font-size: 0.68rem;
  font-weight: 700;
  padding: 2px 8px;
  border-radius: 6px;
  margin-bottom: 8px;
}
.megamenu-featured-badge--new {
  background: #10b981;
}
.megamenu-featured-card h5 {
  font-size: 0.92rem;
  font-weight: 700;
  color: #1e293b;
  margin: 0 0 6px 0;
}
.megamenu-featured-card p {
  font-size: 0.8rem;
  color: #64748b;
  margin: 0 0 10px 0;
  line-height: 1.5;
}
.megamenu-featured-link {
  font-size: 0.8rem;
  font-weight: 600;
  color: #28b5c1;
  text-decoration: none;
  transition: color 0.2s;
}
.megamenu-featured-link:hover {
  color: #1e9aa5;
}

/* ---- Nav Actions ---- */
.nav-actions {
  display: flex;
  align-items: center;
  gap: 12px;
  flex-shrink: 0;
}
.language-dropdown {
  position: relative;
}
.dropdown-toggle {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 6px 12px;
  border: 1px solid #e2e8f0;
  background: #fff;
  border-radius: 10px;
  cursor: pointer;
  font-weight: 600;
  font-size: 0.82rem;
  color: #475569;
  transition: border-color 0.2s, box-shadow 0.2s;
}
.dropdown-toggle:hover {
  border-color: #28b5c1;
  box-shadow: 0 0 0 3px rgba(40,181,193,0.08);
}
.dropdown-arrow {
  width: 14px;
  height: 14px;
  transition: transform 0.2s;
}
.dropdown-content {
  position: absolute;
  top: calc(100% + 6px);
  right: 0;
  background: #fff;
  border-radius: 12px;
  box-shadow: 0 8px 32px rgba(0,0,0,0.12);
  padding: 6px;
  min-width: 90px;
  z-index: 1002;
  display: none;
  border: 1px solid #f1f5f9;
}
.language-dropdown.show .dropdown-content {
  display: block;
}
.dropdown-item {
  display: block;
  width: 100%;
  padding: 7px 14px;
  border: none;
  background: none;
  text-align: left;
  cursor: pointer;
  border-radius: 8px;
  font-weight: 600;
  font-size: 0.82rem;
  color: #475569;
  transition: background 0.15s, color 0.15s;
}
.dropdown-item:hover {
  background: #f1f0ff;
  color: #28b5c1;
}

/* CTA Button */
.cta-btn {
  background: linear-gradient(135deg, #28b5c1, #1e9aa5);
  color: #fff;
  padding: 9px 22px;
  border: none;
  border-radius: 10px;
  font-weight: 700;
  font-size: 0.88rem;
  cursor: pointer;
  transition: transform 0.18s, box-shadow 0.18s;
  box-shadow: 0 2px 8px rgba(40,181,193,0.25);
  white-space: nowrap;
}
.cta-btn:hover {
  transform: translateY(-2px);
  box-shadow: 0 6px 20px rgba(40,181,193,0.35);
}

/* ---- Hamburger ---- */
.hamburger {
  display: none;
  flex-direction: column;
  gap: 5px;
  background: none;
  border: none;
  cursor: pointer;
  padding: 6px;
}
.hamburger span {
  display: block;
  width: 22px;
  height: 2px;
  background: #334155;
  border-radius: 2px;
  transition: transform 0.3s, opacity 0.3s;
}
.hamburger.active span:nth-child(1) { transform: translateY(7px) rotate(45deg); }
.hamburger.active span:nth-child(2) { opacity: 0; }
.hamburger.active span:nth-child(3) { transform: translateY(-7px) rotate(-45deg); }

/* ---- Mobile Menu ---- */
.mobile-menu {
  display: none;
  position: fixed;
  top: 68px;
  left: 0;
  right: 0;
  bottom: 0;
  background: #fff;
  z-index: 999;
  overflow-y: auto;
  padding: 20px;
}
.mobile-menu-inner {
  max-width: 400px;
  margin: 0 auto;
}
.mobile-menu-section {
  margin-bottom: 24px;
}
.mobile-menu-heading {
  font-size: 0.72rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  color: #94a3b8;
  margin-bottom: 10px;
}
.mobile-menu-item {
  display: block;
  padding: 12px 14px;
  color: #334155;
  text-decoration: none;
  font-weight: 500;
  font-size: 0.95rem;
  border-radius: 10px;
  transition: background 0.15s, color 0.15s;
}
.mobile-menu-item:hover {
  background: #f1f0ff;
  color: #28b5c1;
}
.mobile-cta {
  width: 100%;
  margin-top: 12px;
  text-align: center;
}
.mobile-menu-enter-active,
.mobile-menu-leave-active {
  transition: opacity 0.25s;
}
.mobile-menu-enter,
.mobile-menu-leave-to {
  opacity: 0;
}

/* ---- Responsive ---- */
@media (max-width: 1023px) {
  .nav-links { display: none !important; }
  .hamburger { display: flex !important; }
  .mobile-menu { display: block; }
  .nav-container { padding: 0 1.2rem; }
}
@media (max-width: 640px) {
  .nav-actions .language-dropdown { display: none; }
  .cta-btn { font-size: 0.82rem; padding: 8px 16px; }
}
</style>

